import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { AlertTriangleIcon, ChevronDownIcon, RefreshCwIcon } from "lucide-react";
import { z } from "zod";
import { getPricingsheetApiV1PricingsheetGet } from "@/client";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LoadingComponent } from "@/components/LoadingComponent";
import { PricesheetTable } from "@/components/PricesheetTable/PricesheetTable";
import { checkPermission, getErrorDetails } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/pricesheet")({
  component: PricesheetPage,
  beforeLoad: () => {
    checkPermission("pricesheet:read");
  },
});

const PricesheetSchema = z.object({
  period: z.string(),
  bid: z.union([z.number(), z.null()]).optional(),
  offer: z.union([z.number(), z.null()]).optional(),
  settle: z.union([z.number(), z.null()]).optional(),
  delta: z.union([z.number(), z.null()]).optional(),
  product_name: z.string(),
});

const PricesheetArraySchema = z.array(PricesheetSchema);

export type PricesheetArray = z.infer<typeof PricesheetArraySchema>;

const getRefetchInterval = (timestamp: string | undefined) => {
  if (!timestamp) {
    return 60000;
  }

  const lastUpdate = new Date(timestamp);
  const now = new Date();

  const nowAmsterdam = new Date(now.toLocaleString("en-US", { timeZone: "Europe/Amsterdam" }));

  const timeSinceUpdate = nowAmsterdam.getTime() - lastUpdate.getTime();
  const expectedNextUpdate = lastUpdate.getTime() + 60000; // 1 minute after last update
  const timeUntilNextUpdate = expectedNextUpdate - nowAmsterdam.getTime();

  if (timeSinceUpdate < 50000) {
    return Math.max(timeUntilNextUpdate, 1000);
  }

  if (timeSinceUpdate < 90000) {
    return 15000;
  }

  return 30000;
};

function StalePricingWarning({ staleCount }: { staleCount: number }) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button className="mb-2 flex w-full cursor-pointer items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-left text-sm text-amber-600 transition-colors hover:bg-amber-500/15 dark:border-amber-400/20 dark:bg-amber-400/5 dark:text-amber-400 dark:hover:bg-amber-400/10">
          <AlertTriangleIcon className="h-4 w-4 shrink-0" />
          <span className="font-medium">{t("Pricing Data Stale")}</span>
          <ChevronDownIcon
            className={`ml-auto h-4 w-4 shrink-0 opacity-60 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mb-2 rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-sm text-amber-600/80 dark:border-amber-400/10 dark:bg-amber-400/5 dark:text-amber-400/70">
          {t("The pricing sheet has not changed for {{count}} consecutive updates. We are investigating the issue.", {
            count: staleCount,
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default function PricesheetPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isLoading, error, isRefetching, isError } = useQuery({
    queryKey: ["pricesheet"],
    queryFn: () => getPricingsheetApiV1PricingsheetGet(),
    refetchInterval: (query) => {
      const currentData = query.state.data;
      return getRefetchInterval(currentData?.metadata?.timestamp);
    },
    refetchIntervalInBackground: true,
    staleTime: 60000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const handleManualRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ["pricesheet"] });
  };

  if (isError) {
    const errorMessage = getErrorDetails(error);
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center text-center">
        <div className="mb-4 text-red-500">{errorMessage}</div>
        <Button onClick={handleManualRefresh} variant={"outline"}>
          {t("Try again")}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingComponent />;
  }

  if (error) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center text-center">
        <div className="mb-4 text-red-500">{t("Error loading pricesheet")}</div>
        <Button onClick={handleManualRefresh} variant={"outline"}>
          {t("Try again")}
        </Button>
      </div>
    );
  }

  const POWER_BASE_DATA = data?.data?.filter((item) => item.product_name === t("POWER BASELOAD"));
  const POWER_PEAK_DATA = data?.data?.filter((item) => item.product_name === t("POWER PEAKLOAD"));
  const TTF_DATA = data?.data?.filter((item) => item.product_name === "TTF");

  const STALE_THRESHOLD = 5;
  const staleCount = data?.metadata?.stale_count ?? 0;
  const isStale = staleCount >= STALE_THRESHOLD;

  return (
    <div className="flex flex-col">
      {isStale && <StalePricingWarning staleCount={staleCount} />}
      <Tabs defaultValue="ttf" className="flex flex-col gap-2">
        <div className="flex items-center justify-center gap-1 px-2 text-[10px] font-bold sm:gap-2 sm:text-sm">
          <span className="text-center">
            {t("Updated At")}: {format(new Date((data?.metadata.timestamp || "") + "Z"), "yyyy-MM-dd HH:mm:ss")}
          </span>
          <Button
            size={"sm"}
            onClick={handleManualRefresh}
            disabled={isRefetching}
            variant="ghost"
            className="h-6 w-6 p-0 sm:h-8 sm:w-8"
          >
            <RefreshCwIcon className={`h-3 w-3 sm:h-4 sm:w-4 ${isRefetching ? "animate-spin" : ""}`} />
          </Button>
        </div>
        <TabsList className="flex justify-between">
          <TabsTrigger className="w-1/3 px-1 text-[10px] sm:px-3 sm:text-sm" value="power-base">
            {t("Power Base")}
          </TabsTrigger>
          <TabsTrigger className="w-1/3 px-1 text-[10px] sm:px-3 sm:text-sm" value="power-peak">
            {t("Power Peak")}
          </TabsTrigger>
          <TabsTrigger className="w-1/3 px-1 text-[10px] sm:px-3 sm:text-sm" value="ttf">
            {t("TTF")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="ttf">
          <h1 className="text-center text-base font-semibold sm:text-xl">{t("TTF Pricesheet")}</h1>
          <PricesheetTable data={TTF_DATA || []} />
        </TabsContent>
        <TabsContent value="power-base">
          <h1 className="text-center text-base font-semibold sm:text-xl">{t("Power Base Pricesheet")}</h1>
          <PricesheetTable data={POWER_BASE_DATA || []} />
        </TabsContent>
        <TabsContent value="power-peak">
          <h1 className="text-center text-base font-semibold sm:text-xl">{t("Power Peak Pricesheet")}</h1>
          <PricesheetTable data={POWER_PEAK_DATA || []} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
